import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ErrorBoundary from 'components/providers/ErrorBoundary';
import EventProvider from 'components/providers/EventProvider';

import MapInternal from './components/MapInternal';
import {
    DEFAULT_CENTER,
    DEFAULT_MAX_ZOOM,
    DEFAULT_MIN_ZOOM,
    DEFAULT_ZOOM,
    MAP_STYLE,
    TEST_ID
} from './constants';
import MapProvider from './contexts/MapProvider';
import type { MapInteractionOptions, MapOptions, Props } from './types';

const DEFAULT_MAP_OPTIONS: MapOptions = {
    accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
    center: DEFAULT_CENTER,
    centerOffset: [0, 0],
    mapStyle: MAP_STYLE,
    maxZoom: DEFAULT_MAX_ZOOM,
    minZoom: DEFAULT_MIN_ZOOM,
    zoom: DEFAULT_ZOOM
};

const DEFAULT_MAP_INTERACTION_OPTIONS: MapInteractionOptions = {
    hasDrawingControls: false,
    hasMapControls: false
};

const Map = ({
    className = '',
    clearRequestToken,
    commitRequestToken,
    drawingMode,
    drawingTool,
    flightPaths,
    flyToPoint = null,
    focusGeometry,
    geometry,
    homePoint,
    homePointMarkerAriaLabel,
    isBoundaryModified,
    isPickingPoint,
    mapInteractionOptions,
    mapOptions,
    onClickEditMap,
    onClose,
    onComplete,
    onFeatureSelect,
    onFlew,
    onPickPoint,
    prescriptionBounds = null,
    prescriptionZones = null,
    selectedFlight,
    telemetryPointsByFlightId,
    testId = TEST_ID
}: Props) => {
    const [mapId] = useState(uuidv4().substring(0, 8));

    const resolvedMapOptions: MapOptions = {
        ...DEFAULT_MAP_OPTIONS,
        ...mapOptions
    };

    const resolvedMapInteractionOptions: MapInteractionOptions = {
        ...DEFAULT_MAP_INTERACTION_OPTIONS,
        ...mapInteractionOptions
    };

    return (
        <EventProvider eventPrefix={mapId}>
            <MapProvider mapOptions={resolvedMapOptions}>
                <ErrorBoundary>
                    <MapInternal
                        className={className}
                        clearRequestToken={clearRequestToken}
                        commitRequestToken={commitRequestToken}
                        drawingMode={drawingMode}
                        drawingTool={drawingTool}
                        flightPaths={flightPaths}
                        flyToPoint={flyToPoint}
                        focusGeometry={focusGeometry}
                        geometry={geometry}
                        homePoint={homePoint}
                        homePointMarkerAriaLabel={homePointMarkerAriaLabel}
                        isBoundaryModified={isBoundaryModified}
                        isPickingPoint={isPickingPoint}
                        mapInteractionOptions={resolvedMapInteractionOptions}
                        mapOptions={resolvedMapOptions}
                        onClickEditMap={onClickEditMap}
                        onClose={onClose}
                        onComplete={onComplete}
                        onFeatureSelect={onFeatureSelect}
                        onFlew={onFlew}
                        onPickPoint={onPickPoint}
                        prescriptionBounds={prescriptionBounds}
                        prescriptionZones={prescriptionZones}
                        selectedFlight={selectedFlight}
                        telemetryPointsByFlightId={telemetryPointsByFlightId}
                        testId={testId}
                    />
                </ErrorBoundary>
            </MapProvider>
        </EventProvider>
    );
};

export default Map;
